// The match: players, fares, money, health, upgrades, timer. Pure rules, no rendering.
// In multiplayer the host runs the shared parts (fares, timer, offers); every client simulates
// its own car and reports it. Bots are simulated by whoever owns them (the host, or the AI player).

import { Rng } from './rng';
import { Ground } from './ground';
import { DEFAULT_TUNING, newCarState, stepCar, type CarInput, type CarState, type CarTuning, type ImpactContact } from './car';
import { buildDestinations, buildPickupPoints, createFare, farePayout, FARE, type Fare, type Landmark, type PickupPoint } from './fares';
import { computePerks, rankBucket, REROLL_COSTS, rollOffer, UPGRADE_BY_ID, type Perks, type Upgrade } from './upgrades';

export type PlayerKind = 'human' | 'bot' | 'ai' | 'remote';

export interface PlayerStats { fares: number; failed: number; explosions: number; pedestrians: number; redLightsObeyed: number; redLightsRun: number; karmaMin: number; karmaMax: number; }

export interface Offer { id: number; cards: Upgrade[]; rerolls: number; expiresAt: number; }

export interface PlayerState {
  id: string; name: string; colour: string; kind: PlayerKind;
  car: CarState;
  money: number; health: number; maxHealth: number; karma: number;
  owned: Map<string, number>; perks: Perks; tuning: CarTuning;
  riding: Fare[];
  stats: PlayerStats;
  respawnAt: number | null;
  offer: Offer | null;
  nitroUntil: number; nitroReadyAt: number;
  jetReadyAt: number; ghostUntil: number; ghostReadyAt: number; freezeUntil: number; freezeReadyAt: number;
  invulnUntil: number;
  bumpCooldown: number;
  lastImpactAt: number;
  input: CarInput;
}

export type MatchEvent =
  | { t: 'pickup'; pid: string; fare: Fare }
  | { t: 'dropoff'; pid: string; fare: Fare; total: number; tip: number; smooth: number }
  | { t: 'fail'; pid: string; fare: Fare; reason: 'timeout' | 'explode' }
  | { t: 'damage'; pid: string; amount: number; kind: string; contact?: ImpactContact }
  | { t: 'explode'; pid: string }
  | { t: 'respawn'; pid: string }
  | { t: 'offer'; pid: string; offer: Offer }
  | { t: 'upgrade'; pid: string; id: string }
  | { t: 'rush' }
  | { t: 'end' }
  | { t: 'countdown'; n: number }
  | { t: 'go' };

import { DEFAULT_FLAGS, type Flags } from './flags';
export interface MatchConfig { durationSec: number; seed: number; countdownSec: number; flags?: Flags; }
export const DEFAULT_MATCH: MatchConfig = { durationSec: 600, seed: 1, countdownSec: 3 };

export class Match {
  readonly rng: Rng;
  readonly players = new Map<string, PlayerState>();
  readonly fares: Fare[] = [];
  readonly pickups: PickupPoint[];
  readonly dests: { name: string; x: number; z: number }[];
  readonly events: MatchEvent[] = [];
  /** seconds since the match clock started (negative during countdown) */
  time: number;
  phase: 'countdown' | 'playing' | 'ended' = 'countdown';
  rush = false;
  /** false on clients that mirror the host: only their own car is simulated, shared rules are skipped */
  authority = true;
  private spawnCache = new Map<string, { x: number; z: number; yaw: number }>();
  private nextFareId = 1;
  private nextOfferId = 1;
  private lastCountdown = 99;

  constructor(readonly ground: Ground, readonly bounds: { x: [number, number]; z: [number, number] }, readonly ranks: { name: string; p: [number, number, number] }[],
    landmarks: Landmark[], busStops: { p: [number, number, number] }[], readonly config: MatchConfig = DEFAULT_MATCH) {
    this.rng = new Rng(config.seed);
    this.pickups = buildPickupPoints(ground, landmarks, busStops);
    this.dests = buildDestinations(ground, landmarks);
    this.time = -config.countdownSec;
  }

  // ------------------------------------------------------------------ players
  addPlayer(id: string, name: string, colour: string, kind: PlayerKind, rankIndex?: number): PlayerState {
    const rank = this.ranks[(rankIndex ?? this.players.size) % this.ranks.length];
    const spot = this.spawnSpot(rank.p[0], rank.p[2], this.players.size);
    const car = newCarState(spot.x, this.ground.heightAt(spot.x, spot.z), spot.z, spot.yaw);
    const owned = new Map<string, number>();
    const perks = computePerks(owned, DEFAULT_TUNING);
    const p: PlayerState = {
      id, name, colour, kind, car, money: 0, health: 100, maxHealth: 100, karma: 0, owned, perks, tuning: { ...DEFAULT_TUNING, ...perks.tuning },
      riding: [], stats: { fares: 0, failed: 0, explosions: 0, pedestrians: 0, redLightsObeyed: 0, redLightsRun: 0, karmaMin: 0, karmaMax: 0 },
      respawnAt: null, offer: null, nitroUntil: 0, nitroReadyAt: 0, jetReadyAt: 0, ghostUntil: 0, ghostReadyAt: 0, freezeUntil: 0, freezeReadyAt: 0,
      invulnUntil: 0, bumpCooldown: 0, lastImpactAt: -9,
      input: { steer: 0, brake: 0, nitro: false, throttle: 0 },
    };
    this.players.set(id, p);
    return p;
  }

  removePlayer(id: string): void {
    const p = this.players.get(id); if (!p) return;
    for (const f of p.riding) { f.state = 'waiting'; f.rider = null; f.timeLeft = f.timeAllowed; }
    this.players.delete(id);
  }

  private spawnSpot(x: number, z: number, slot: number): { x: number; z: number; yaw: number } {
    const key = `${Math.round(x)}:${Math.round(z)}`;
    let spot: { x: number; z: number; yaw: number } | null = this.spawnCache.get(key) ?? null;
    if (!spot) {
      spot = this.ground.bestSpawnNear(x, z, 70);
      if (!spot) { const r = this.ground.nearestRoad(x, z, 60) ?? { x, z }; const axis = this.ground.roadHeading(r.x, r.z); spot = { x: r.x, z: r.z, yaw: this.ground.clearRun(r.x, r.z, axis) >= this.ground.clearRun(r.x, r.z, axis + Math.PI) ? axis : axis + Math.PI }; }
      this.spawnCache.set(key, spot);
    }
    const r = spot; const yaw = spot.yaw;
    // stagger along the road so several taxis can start from one rank, skipping spots that touch walls
    const candidates = [0, -6, 6, -12, 12, -18, 18, -24];
    let k = 0;
    for (const along of candidates) {
      const sx = r.x - Math.sin(yaw) * along, sz = r.z - Math.cos(yaw) * along;
      if (!this.ground.isRoad(sx, sz) || this.ground.blockedNear(sx, sz, 2.2)) continue;
      if (k === slot % candidates.length) return { x: sx, z: sz, yaw };
      k++;
    }
    return { x: r.x, z: r.z, yaw };
  }

  nearestRank(x: number, z: number): { x: number; z: number; yaw: number } {
    let best = this.ranks[0], bd = Infinity;
    for (const r of this.ranks) { const d = Math.hypot(r.p[0] - x, r.p[2] - z); if (d < bd) { bd = d; best = r; } }
    return this.spawnSpot(best.p[0], best.p[2], 0);
  }

  ranking(): PlayerState[] { return [...this.players.values()].sort((a, b) => b.money - a.money); }
  rankOf(pid: string): number { return this.ranking().findIndex((p) => p.id === pid); }
  timeLeft(): number { return Math.max(0, this.config.durationSec - Math.max(0, this.time)); }

  // ------------------------------------------------------------------ stepping
  /** Advance the match. `simulate` lists the players whose cars this process owns (their inputs are read from p.input). */
  step(dt: number, simulate: Iterable<string>, extraCircles: { x: number; z: number; r: number; vx: number; vz: number }[] = []): void {
    const prevTime = this.time;
    this.time += dt;
    if (this.phase === 'countdown' && this.authority) {
      const n = Math.ceil(-this.time);
      if (n !== this.lastCountdown && n > 0) { this.lastCountdown = n; this.events.push({ t: 'countdown', n }); }
      if (this.time >= 0) { this.phase = 'playing'; this.events.push({ t: 'go' }); }
    }
    if (this.authority && this.phase === 'playing' && !this.rush && this.timeLeft() <= 60) { this.rush = true; this.events.push({ t: 'rush' }); }
    if (this.authority && this.phase === 'playing' && this.timeLeft() <= 0 && prevTime < this.config.durationSec) { this.phase = 'ended'; this.events.push({ t: 'end' }); }

    const moving = this.phase === 'playing';
    const others = [...this.players.values()].map((p) => ({ id: p.id, x: p.car.x, z: p.car.z, r: p.tuning.radius + p.tuning.halfLength * 0.5, vx: p.car.vx, vz: p.car.vz, ghost: p.ghostUntil > this.time }));
    for (const pid of simulate) {
      const p = this.players.get(pid); if (!p) continue;
      if (p.respawnAt != null) { if (this.time >= p.respawnAt) this.respawn(p); else { p.car.vx = p.car.vz = 0; continue; } }
      const inp = p.input;
      const eff: CarInput = { steer: inp.steer, brake: moving ? inp.brake : 0, nitro: p.nitroUntil > this.time, throttle: moving ? inp.throttle : 0 };
      if (inp.nitro && p.perks.nitro && this.time >= p.nitroReadyAt) { p.nitroUntil = this.time + 2; p.nitroReadyAt = this.time + 10; }
      const ghosting = p.ghostUntil > this.time;
      const rivals = ghosting ? [] : [...others.filter((o) => o.id !== pid && !o.ghost), ...extraCircles];
      stepCar(p.car, eff, p.tuning, this.ground, this.bounds, dt, rivals);
      // damage
      if (p.car.impact > 7 && this.time > p.invulnUntil && this.time - p.lastImpactAt > 0.25) {
        p.lastImpactAt = this.time;
        const dmg = Math.round(Math.max(3, p.car.impact) * (p.car.impactKind === 'car' ? 0.5 : 1) * p.perks.damageTaken);
        if (dmg > 0) this.damage(p, dmg, p.car.impactKind);
        for (const f of p.riding) f.collisions++;
      }
      if (p.perks.regen > 0 && p.health > 0) p.health = Math.min(p.maxHealth, p.health + p.perks.regen * dt);
    }

    if (this.phase !== 'playing' || !this.authority) return;
    // fares: timers, pickups, drop-offs
    for (const f of this.fares) {
      if (f.state !== 'riding' || !f.rider) continue;
      const p = this.players.get(f.rider); if (!p) { f.state = 'waiting'; f.rider = null; continue; }
      if (p.freezeUntil <= this.time) f.timeLeft -= dt;
      if (f.timeLeft <= 0) this.failFare(p, f, 'timeout');
    }
    for (const p of this.players.values()) {
      if (p.respawnAt != null) continue;
      const slow = Math.abs(p.car.speed) < 3;
      if (p.riding.length < p.perks.capacity && slow) {
        const r = FARE.pickupRadius * p.perks.ringScale;
        for (const f of this.fares) {
          if (f.state !== 'waiting') continue;
          if (Math.hypot(f.pickup.x - p.car.x, f.pickup.z - p.car.z) < r) { this.pickup(p, f); break; }
        }
      }
      for (const f of [...p.riding]) {
        const r = FARE.dropRadius * p.perks.ringScale + p.perks.dropRadiusBonus;
        if (Math.hypot(f.dest.x - p.car.x, f.dest.z - p.car.z) < r && (slow || p.perks.dropRadiusBonus > 0)) this.dropoff(p, f);
      }
      if (p.offer && this.time > p.offer.expiresAt) p.offer = null;
    }
    this.topUpFares();
  }

  /** Overwrite a remote player's car (and its self-reported health/respawn) from the network. */
  setRemoteCar(pid: string, car: Partial<CarState>, hp?: number, respawnAt?: number | null): void {
    const p = this.players.get(pid); if (!p) return;
    Object.assign(p.car, car);
    if (hp != null) p.health = hp;
    if (respawnAt !== undefined && this.authority) {
      if (respawnAt != null && p.respawnAt == null) { p.respawnAt = respawnAt; p.stats.explosions++; for (const f of [...p.riding]) this.failFare(p, f, 'explode'); this.events.push({ t: 'explode', pid }); }
      else if (respawnAt == null && p.respawnAt != null) { p.respawnAt = null; this.events.push({ t: 'respawn', pid }); }
    }
  }

  // ------------------------------------------------------------------ fares
  private topUpFares(): void {
    const humans = Math.max(1, this.players.size);
    const want = Math.min(24, 2 * humans + 1);
    let waiting = this.fares.filter((f) => f.state === 'waiting').length;
    let guard = 0;
    while (waiting < want && guard++ < 20) {
      // spawn near a random player so nobody has to drive across town for their first fare
      const ps = [...this.players.values()];
      const near = ps.length ? ps[this.rng.int(ps.length)] : null;
      const f = createFare(this.rng, this.nextFareId++, this.pickups, this.dests, near ? { x: near.car.x, z: near.car.z, minDist: 40, maxDist: 220 } : undefined);
      if (this.fares.some((o) => o.state === 'waiting' && o.pickup.id === f.pickup.id)) continue;
      this.fares.push(f); waiting++;
    }
    // prune finished fares to keep the list small
    if (this.fares.length > 80) { const keep = this.fares.filter((f) => f.state === 'waiting' || f.state === 'riding'); this.fares.length = 0; this.fares.push(...keep); }
  }

  private pickup(p: PlayerState, f: Fare): void {
    f.state = 'riding'; f.rider = p.id; f.timeAllowed = Math.round(f.timeAllowed * (1 + p.perks.timerBonus)); f.timeLeft = f.timeAllowed; f.collisions = 0;
    p.riding.push(f);
    this.events.push({ t: 'pickup', pid: p.id, fare: f });
  }

  private dropoff(p: PlayerState, f: Fare): void {
    f.state = 'done'; p.riding = p.riding.filter((x) => x !== f);
    const pay = farePayout(f, { karma: p.karma, rush: this.rush, tipBonus: p.perks.fareBonus });
    p.money += pay.total; p.stats.fares++;
    this.events.push({ t: 'dropoff', pid: p.id, fare: f, ...pay });
    this.makeOffer(p);
  }

  private failFare(p: PlayerState, f: Fare, reason: 'timeout' | 'explode'): void {
    f.state = 'failed'; p.riding = p.riding.filter((x) => x !== f); p.stats.failed++;
    this.events.push({ t: 'fail', pid: p.id, fare: f, reason });
  }

  // ------------------------------------------------------------------ health
  damage(p: PlayerState, amount: number, kind: string): void {
    if (p.health <= 0 || p.respawnAt != null) return;
    p.health = Math.max(0, p.health - amount);
    this.events.push({ t: 'damage', pid: p.id, amount, kind, ...(p.car.impactContact ? { contact: { ...p.car.impactContact } } : {}) });
    if (p.health <= 0) this.explode(p);
  }

  /** Disaster damage: never below 1 HP. */
  disasterDamage(p: PlayerState, amount: number): void {
    if (p.respawnAt != null) return;
    const a = Math.round(amount * p.perks.disasterDamage);
    p.health = Math.max(1, p.health - a);
    this.events.push({ t: 'damage', pid: p.id, amount: a, kind: 'disaster' });
  }

  private explode(p: PlayerState): void {
    p.stats.explosions++;
    if (this.authority) for (const f of [...p.riding]) this.failFare(p, f, 'explode');
    p.respawnAt = this.time + 4;
    p.car.vx = p.car.vz = 0;
    this.events.push({ t: 'explode', pid: p.id });
  }

  private respawn(p: PlayerState): void {
    const spot = this.nearestRank(p.car.x, p.car.z);
    Object.assign(p.car, newCarState(spot.x, this.ground.heightAt(spot.x, spot.z), spot.z, spot.yaw));
    p.health = p.maxHealth; p.respawnAt = null; p.invulnUntil = this.time + 2;
    this.events.push({ t: 'respawn', pid: p.id });
  }

  // ------------------------------------------------------------------ upgrades
  get flags(): Flags { return this.config.flags ?? DEFAULT_FLAGS; }

  private makeOffer(p: PlayerState): void {
    if (!this.flags.upgrades) return;
    const bucket = rankBucket(this.rankOf(p.id), this.players.size);
    const cards = rollOffer(this.rng, p.owned, bucket, 3);
    if (!cards.length) return;
    p.offer = { id: this.nextOfferId++, cards, rerolls: 0, expiresAt: this.time + 12 };
    this.events.push({ t: 'offer', pid: p.id, offer: p.offer });
  }

  rerollCost(p: PlayerState): number { const c = REROLL_COSTS[Math.min(REROLL_COSTS.length - 1, p.offer?.rerolls ?? 0)]; return p.perks.rerollHalf ? Math.round(c / 2) : c; }

  reroll(pid: string): boolean {
    const p = this.players.get(pid); if (!p?.offer) return false;
    const cost = this.rerollCost(p); if (p.money < cost) return false;
    p.money -= cost;
    const bucket = rankBucket(this.rankOf(p.id), this.players.size);
    p.offer = { id: this.nextOfferId++, cards: rollOffer(this.rng, p.owned, bucket, 3), rerolls: p.offer.rerolls + 1, expiresAt: this.time + 12 };
    this.events.push({ t: 'offer', pid: p.id, offer: p.offer });
    return true;
  }

  skipOffer(pid: string): void { const p = this.players.get(pid); if (p) p.offer = null; }

  chooseUpgrade(pid: string, upgradeId: string): boolean {
    const p = this.players.get(pid); if (!p?.offer) return false;
    if (!p.offer.cards.some((c) => c.id === upgradeId)) return false;
    this.applyUpgrade(p, upgradeId);
    p.offer = null;
    return true;
  }

  applyUpgrade(p: PlayerState, upgradeId: string): void {
    const u = UPGRADE_BY_ID.get(upgradeId); if (!u) return;
    p.owned.set(u.id, (p.owned.get(u.id) ?? 0) + 1);
    p.perks = computePerks(p.owned, DEFAULT_TUNING);
    p.tuning = { ...DEFAULT_TUNING, ...p.perks.tuning };
    const prevMax = p.maxHealth; p.maxHealth = p.perks.maxHealth;
    if (u.id === 'spare') p.health = Math.min(p.maxHealth, p.health + 25 + (p.maxHealth - prevMax));
    this.events.push({ t: 'upgrade', pid: p.id, id: u.id });
  }

  /** Active abilities bound to the nitro button when owned. */
  useAbility(pid: string): void {
    const p = this.players.get(pid); if (!p || p.respawnAt != null) return;
    if (p.perks.jet && this.time >= p.jetReadyAt && !p.car.airborne) { p.car.airborne = true; p.car.vy = 9; p.jetReadyAt = this.time + 6; return; }
    if (p.perks.ghost && this.time >= p.ghostReadyAt) { p.ghostUntil = this.time + 5; p.ghostReadyAt = this.time + 25; return; }
    if (p.perks.meterFreeze && this.time >= p.freezeReadyAt) { p.freezeUntil = this.time + 8; p.freezeReadyAt = this.time + 30; return; }
  }

  drainEvents(): MatchEvent[] { const e = this.events.splice(0); return e; }

  // ------------------------------------------------------------------ network snapshots (host -> clients)
  snapshot(): MatchSnapshot {
    return {
      time: this.time, phase: this.phase, rush: this.rush,
      fares: this.fares.filter((f) => f.state === 'waiting' || f.state === 'riding').map((f) => ({ id: f.id, passenger: f.passenger, pickup: f.pickup, dest: f.dest, timeAllowed: f.timeAllowed, base: f.base, state: f.state, rider: f.rider, timeLeft: Math.round(f.timeLeft * 10) / 10, collisions: f.collisions })),
      players: [...this.players.values()].map((p) => ({ id: p.id, money: p.money, health: Math.round(p.health), maxHealth: p.maxHealth, karma: Math.round(p.karma), respawnAt: p.respawnAt, owned: [...p.owned.entries()], stats: p.stats, offer: p.offer ? { id: p.offer.id, cards: p.offer.cards.map((c) => c.id), rerolls: p.offer.rerolls, expiresAt: p.offer.expiresAt } : null })),
    };
  }

  applySnapshot(s: MatchSnapshot, localId: string): void {
    this.time = s.time; this.rush = s.rush;
    if (s.phase !== this.phase) { this.phase = s.phase; if (s.phase === 'playing') this.events.push({ t: 'go' }); if (s.phase === 'ended') this.events.push({ t: 'end' }); }
    const byId = new Map(this.fares.map((f) => [f.id, f]));
    const seen = new Set<number>();
    for (const sf of s.fares) {
      seen.add(sf.id);
      let f = byId.get(sf.id);
      if (!f) { f = { ...sf } as Fare; this.fares.push(f); } else Object.assign(f, sf);
    }
    for (const f of this.fares) if (!seen.has(f.id) && (f.state === 'waiting' || f.state === 'riding')) f.state = 'done';
    for (const sp of s.players) {
      const p = this.players.get(sp.id); if (!p) continue;
      p.money = sp.money; p.maxHealth = sp.maxHealth; p.karma = sp.karma; p.stats = sp.stats;
      if (sp.id !== localId) { p.health = sp.health; p.respawnAt = sp.respawnAt; }
      const ownedNow = new Map(sp.owned);
      if ([...ownedNow].some(([k, v]) => p.owned.get(k) !== v)) { p.owned = ownedNow; p.perks = computePerks(p.owned, DEFAULT_TUNING); p.tuning = { ...DEFAULT_TUNING, ...p.perks.tuning }; }
      p.riding = this.fares.filter((f) => f.state === 'riding' && f.rider === p.id);
      if (sp.id === localId) {
        if (!sp.offer) p.offer = null;
        else if (!p.offer || p.offer.id !== sp.offer.id) { p.offer = { id: sp.offer.id, cards: sp.offer.cards.map((id) => UPGRADE_BY_ID.get(id)!).filter(Boolean), rerolls: sp.offer.rerolls, expiresAt: sp.offer.expiresAt }; this.events.push({ t: 'offer', pid: p.id, offer: p.offer }); }
      }
    }
  }
}

export interface MatchSnapshot {
  time: number; phase: Match['phase']; rush: boolean;
  fares: Omit<Fare, never>[];
  players: { id: string; money: number; health: number; maxHealth: number; karma: number; respawnAt: number | null; owned: [string, number][]; stats: PlayerStats; offer: { id: number; cards: string[]; rerolls: number; expiresAt: number } | null }[];
}
